import { useEffect, useState } from 'react'
import PropTypes from '~/lib/propTypes'
import styled from '@emotion/styled'
import { Button, InputV2 as Input, space, Checkbox } from '@ticketswap/solar'
import { useMutation, useQuery } from '@apollo/client'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { GET_LOGGED_IN_USER } from '~/lib/get-logged-in-user'
import { track } from '~/utils/analytics'
import { AUTHENTICATION_COMPLETED, VERIFICATION_STARTED } from '~/lib/tracking-events'
import { START_EMAIL_VERIFICATION } from '~/graphql/mutations/startEmailVerification'
import { GIVE_USER_CONSENT } from '~/graphql/mutations/giveUserConsent'
import { AGREE_WITH_TERMS_AND_PRIVACY_POLICY } from '~/graphql/mutations/agreeWithTermsAndPrivacyPolicy'
import { deleteCookie, setCookie } from 'cookies-next'

const StyledCheckbox = styled(Checkbox)`
  margin-block-start: ${space[16]};
`

const StyledButton = styled(Button)`
  margin-block-start: ${space[24]};
`

const SignupSocial = ({ t, onSignupComplete, hide, method }) => {
  const [loading, setLoading] = useState(false)
  const [marketingConsentGiven, setMarketingConsentGiven] = useState(false)
  const [giveConsentMutation] = useMutation(GIVE_USER_CONSENT)
  const [startUserEmailVerification] = useMutation(START_EMAIL_VERIFICATION)
  const [agreeWithTermsAndPrivacyPolicy] = useMutation(AGREE_WITH_TERMS_AND_PRIVACY_POLICY)
  const { data = {}, loading: loadingLoggedInUser } = useQuery(GET_LOGGED_IN_USER, {
    skip: !setCookie('token'),
  })
  const { loggedInUser: user } = data

  const formik = useFormik({
    validationSchema: yup.object().shape({
      email: yup.string().email().required(t('common:generic.required')),
    }),
    initialValues: {
      email: user?.email || '',
    },
    isInitialValid: ({ validationSchema, initialValues }) =>
      validationSchema.isValidSync(initialValues),
    onSubmit: async ({ email }) => {
      setLoading(true)

      await startUserEmailVerification({
        variables: {
          input: {
            userId: user.id,
            email,
            redirectTo: null,
          },
        },
      })

      if (marketingConsentGiven) {
        await giveConsentMutation({
          variables: {
            input: {
              consentType: 'MARKETING_EMAIL',
              hasGivenConsent: true,
            },
          },
        })
      }

      track(VERIFICATION_STARTED)
      await agreeWithTermsAndPrivacyPolicy()
      onSignupComplete()
    },
  })

  useEffect(() => {
    track(AUTHENTICATION_COMPLETED, {
      method,
      isNew: true,
    })
  }, [method])

  useEffect(() => {
    if (!Boolean(user) && !loadingLoggedInUser && hide) {
      // This happens when the user tries to register with facebook
      // while they have an outdated cookie in their token.
      // For now we will close the modal and hope that the user
      // tries to register again.
      deleteCookie('token')
      hide()
    }
  }, [user, loadingLoggedInUser, hide])

  const { handleSubmit, values, handleChange, isValid } = formik

  return (
    <form onSubmit={handleSubmit}>
      <Input
        placeholder={t('common:auth.signupFacebook.placeholder')}
        type="email"
        name="email"
        id="email"
        label={t(
          user?.email === null
            ? 'common:auth.signupFacebook.label.unknown'
            : 'common:auth.signupFacebook.label.email'
        )}
        value={values.email}
        onChange={handleChange}
      />

      <StyledCheckbox
        id="marketingConsent"
        name="marketingConsent"
        label={t('common:auth.consent.label')}
        onChange={event => setMarketingConsentGiven(event.target.checked)}
      />

      <StyledButton variant="success" fullWidth type="submit" disabled={!isValid} loading={loading}>
        {t(
          user?.email === null
            ? 'common:auth.signupFacebook.button.unknown'
            : 'common:auth.signupFacebook.button.email'
        )}
      </StyledButton>
    </form>
  )
}

SignupSocial.propTypes = {
  t: PropTypes.func.isRequired,
  onSignupComplete: PropTypes.func.isRequired,
  hide: PropTypes.func,
}

export default SignupSocial
